import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface UICollapseContextType {
  isCollapsed: boolean;
  collapseUI: () => void;
  expandUI: () => void;
  // Track which elements are being hovered
  hoveredElement: string | null;
  setHoveredElement: (element: string | null) => void;
}

const UICollapseContext = createContext<UICollapseContextType | null>(null);

export function UICollapseProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);

  const collapseUI = useCallback(() => {
    // Ensure any temporarily expanded-on-hover element collapses immediately
    setHoveredElement(null);
    setIsCollapsed(true);
  }, []);

  const expandUI = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  // Listen for graph interactions (drag or node click)
  useEffect(() => {
    const handleGraphInteraction = (e: CustomEvent) => {
      if (
        e.detail?.type === 'drag-start' ||
        e.detail?.type === 'node-click' ||
        e.detail?.type === 'node-context' ||
        e.detail?.type === 'pane-click'
      ) {
        collapseUI();
      }
    };

    window.addEventListener('graph-interaction' as any, handleGraphInteraction);
    return () => {
      window.removeEventListener('graph-interaction' as any, handleGraphInteraction);
    };
  }, [collapseUI]);

  return (
    <UICollapseContext.Provider value={{ isCollapsed, collapseUI, expandUI, hoveredElement, setHoveredElement }}>
      {children}
    </UICollapseContext.Provider>
  );
}

export function useUICollapse() {
  const context = useContext(UICollapseContext);
  if (!context) {
    throw new Error('useUICollapse must be used within a UICollapseProvider');
  }
  return context;
}
